import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Download, FileText, Pencil, Plus, Receipt, Repeat, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useImportVendorsMutation,
  useUpsertVendorMutation,
  useVendorCandidatesQuery,
  useVendorsQuery,
} from '@/client/api/business/spend';
import { VENDOR_CATEGORY_LABEL, type BizVendor, type VendorCategory } from '@/client/types/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { Field, filterClass, inputClass, textareaClass } from './components/spend-ui';
import { DeleteConfirmDialog, EmptyState, RowMenu, StatusBadge } from '../HumanResource/components/hr-ui';
import { money, totals } from '../HumanResource/components/comp-format';

type Form = Omit<BizVendor, 'id' | 'status' | 'owed' | 'activeContracts' | 'recurringMonthly' | 'integrationSpend' | 'paymentTermsDays'> & {
  paymentTermsDays: string;
};
const EMPTY: Form = {
  name: '', category: 'saas', integrationName: '', contactName: '', email: '', phone: '', website: '',
  defaultCurrency: 'USD', paymentTermsDays: '', taxId: '', notes: '',
};

function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const candidatesQ = useVendorCandidatesQuery(open);
  const doImport = useImportVendorsMutation();
  const [picked, setPicked] = useState<Record<string, string>>({});
  useEffect(() => {
    if (open) setPicked({});
  }, [open]);
  const candidates = candidatesQ.data ?? [];

  const submit = () =>
    doImport.mutate(
      Object.entries(picked).map(([integrationName, vendorName]) => ({ integrationName, vendorName })),
      {
        onSuccess: (r) => {
          toast.success(`${r.created.length} vendor(s) added, ${r.linked.length} linked to an existing vendor`);
          onClose();
        },
        onError: (e) => toast.error((e as Error).message || 'Import failed'),
      },
    );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add vendors from integrations</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500">
          Third-party services we integrate with and do not have as a vendor yet. Pick the ones we pay; their monthly usage then shows beside them.
          A name that already exists is linked, not duplicated.
        </p>
        {candidatesQ.isLoading ? (
          <p className="py-6 text-sm text-gray-500">Loading integrations…</p>
        ) : candidates.length === 0 ? (
          <p className="py-6 text-sm text-gray-500">Every integration is already a vendor.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {candidates.map((c) => {
              const on = c.integrationName in picked;
              return (
                <li key={c.integrationName} className="flex items-center gap-3 py-2">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setPicked((p) => {
                        const next = { ...p };
                        if (on) delete next[c.integrationName];
                        else next[c.integrationName] = c.vendorName;
                        return next;
                      })
                    }
                    aria-label={`Add ${c.vendorName}`}
                  />
                  <input
                    value={picked[c.integrationName] ?? c.vendorName}
                    disabled={!on}
                    onChange={(e) => setPicked((p) => ({ ...p, [c.integrationName]: e.target.value }))}
                    className={`${inputClass} max-w-xs`}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
                    {c.integrationName}
                    {c.category && ` · ${c.category}`}
                    {c.plan && ` · ${c.plan}`}
                    {c.nameTaken && ' · will link to the existing vendor'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!Object.keys(picked).length || doImport.isPending} onClick={submit}>
            Add {Object.keys(picked).length || ''} vendor{Object.keys(picked).length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VendorsPage() {
  const navigate = useNavigate();
  const vendorsQ = useVendorsQuery();
  const upsert = useUpsertVendorMutation();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [status, setStatus] = useState<'active' | 'archived'>('active');
  const [category, setCategory] = useState<VendorCategory | 'all'>('all');
  const [editing, setEditing] = useState<BizVendor | null | 'new'>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [toggling, setToggling] = useState<BizVendor | null>(null);

  const vendors = vendorsQ.data ?? [];
  const shown = vendors.filter((v) => v.status === status && (category === 'all' || v.category === category));
  const stats = useMemo(() => {
    const active = vendors.filter((v) => v.status === 'active');
    return [
      { key: 'vendors', label: 'Active vendors', value: active.length },
      { key: 'owed', label: 'Owed (unpaid bills)', value: totals(active.flatMap((v) => v.owed ?? [])) },
      { key: 'recurring', label: 'Recurring per month', value: totals(active.flatMap((v) => v.recurringMonthly ?? [])) },
      { key: 'linked', label: 'Linked to an integration', value: active.filter((v) => v.integrationName).length },
    ];
  }, [vendors]);

  useEffect(() => {
    if (editing === null) return;
    if (editing === 'new') setForm(EMPTY);
    else
      setForm({
        name: editing.name, category: editing.category, integrationName: editing.integrationName ?? '', contactName: editing.contactName,
        email: editing.email, phone: editing.phone, website: editing.website, defaultCurrency: editing.defaultCurrency ?? '',
        paymentTermsDays: editing.paymentTermsDays == null ? '' : String(editing.paymentTermsDays), taxId: editing.taxId, notes: editing.notes,
      });
  }, [editing]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    upsert.mutate(
      {
        id: editing && editing !== 'new' ? editing.id : undefined,
        ...form,
        integrationName: form.integrationName?.trim() || null,
        defaultCurrency: form.defaultCurrency?.trim().toUpperCase() || null,
        paymentTermsDays: form.paymentTermsDays === '' ? null : Number(form.paymentTermsDays),
      },
      {
        onSuccess: () => {
          toast.success(editing === 'new' ? 'Vendor added' : 'Vendor updated');
          setEditing(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save the vendor'),
      },
    );
  };

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AccountingPageShell
      mock={false}
      title="Vendors"
      description="Everyone FeelyFeely pays: what we owe them, live contracts, and recurring cost. Integration vendors show this month's metered usage."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Payables' }, { label: 'Vendors' }]}
      icon={Store}
      actions={
        <div className="flex gap-2">
          <PermissionButton code={PERMISSIONS.accounting.manage} variant="outline" className="gap-2" onClick={() => setImportOpen(true)}>
            <Download className="h-4 w-4" /> From integrations
          </PermissionButton>
          <PermissionButton code={PERMISSIONS.accounting.manage} className="gap-2" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" /> Add vendor
          </PermissionButton>
        </div>
      }
      stats={stats}
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'archived')} className={filterClass}>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value as VendorCategory | 'all')} className={filterClass}>
          <option value="all">All categories</option>
          {Object.entries(VENDOR_CATEGORY_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>

      {vendorsQ.isError ? (
        <EmptyState label={`Could not load vendors: ${(vendorsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : vendorsQ.isLoading ? (
        <EmptyState label="Loading vendors…" />
      ) : shown.length === 0 ? (
        <EmptyState label={vendors.length ? 'No vendors match.' : 'No vendors yet. Add one, or bring them in from integrations.'} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Owed</th>
                <th className="px-4 py-3 font-medium">Recurring / month</th>
                <th className="px-4 py-3 font-medium">Usage this month</th>
                <th className="px-4 py-3 font-medium">Contracts</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((v) => (
                <tr key={v.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && setEditing(v)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{v.name}</p>
                    <p className="text-xs text-gray-400">{[v.contactName, v.email].filter(Boolean).join(' · ') || v.website || '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{VENDOR_CATEGORY_LABEL[v.category]}</td>
                  <td className="px-4 py-3 text-gray-900">{totals(v.owed ?? [])}</td>
                  <td className="px-4 py-3 text-gray-600">{totals(v.recurringMonthly ?? [])}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {v.integrationName ? (
                      v.integrationSpend?.amount != null && v.integrationSpend.currency ? (
                        money(v.integrationSpend.amount, v.integrationSpend.currency)
                      ) : (
                        <span className="text-xs text-gray-400">{v.integrationName}: cost not known</span>
                      )
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{v.activeContracts || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      label={`Actions for ${v.name}`}
                      items={[
                        { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => setEditing(v) },
                        { label: 'Bills', icon: Receipt, onSelect: () => navigate(`/admin/accounting/payables/bills?vendor=${v.id}`) },
                        { label: 'Contracts', icon: FileText, onSelect: () => navigate(`/admin/business/contracts?vendor=${v.id}`) },
                        { label: 'Recurring charges', icon: Repeat, onSelect: () => navigate(`/admin/business/recurring?vendor=${v.id}`) },
                        {
                          label: v.status === 'active' ? 'Archive' : 'Restore',
                          icon: v.status === 'active' ? Archive : ArchiveRestore,
                          hidden: !canManage,
                          onSelect: () => setToggling(v),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add vendor' : `Edit ${editing?.name ?? ''}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <input required value={form.name} onChange={set('name')} className={inputClass} />
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={set('category')} className={inputClass}>
                  {Object.entries(VENDOR_CATEGORY_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Contact person">
                <input value={form.contactName} onChange={set('contactName')} className={inputClass} />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email} onChange={set('email')} className={inputClass} />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={set('phone')} className={inputClass} />
              </Field>
              <Field label="Website">
                <input value={form.website} onChange={set('website')} className={inputClass} placeholder="https://" />
              </Field>
              <Field label="Usual currency">
                <input value={form.defaultCurrency ?? ''} maxLength={3} onChange={set('defaultCurrency')} className={inputClass} />
              </Field>
              <Field label="Pay within (days)">
                <input type="number" min={0} max={365} value={form.paymentTermsDays} onChange={set('paymentTermsDays')} className={inputClass} />
              </Field>
              <Field label="Tax ID / TIN">
                <input value={form.taxId} onChange={set('taxId')} className={inputClass} />
              </Field>
              <Field label="Integration (config name)">
                <input value={form.integrationName ?? ''} onChange={set('integrationName')} className={inputClass} placeholder="e.g. serpapi" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea rows={2} value={form.notes} onChange={set('notes')} className={textareaClass} />
            </Field>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />

      <DeleteConfirmDialog
        open={!!toggling}
        onOpenChange={(o) => !o && setToggling(null)}
        title={toggling?.status === 'active' ? `Archive ${toggling?.name}?` : `Restore ${toggling?.name}?`}
        description={
          toggling?.status === 'active'
            ? 'It drops out of pickers. Bills, contracts and history stay. Pause its recurring charges separately.'
            : 'It can be picked for bills and contracts again.'
        }
        actionLabel={toggling?.status === 'active' ? 'Archive' : 'Restore'}
        danger={toggling?.status === 'active'}
        onConfirm={() => {
          if (toggling)
            upsert.mutate(
              { id: toggling.id, status: toggling.status === 'active' ? 'archived' : 'active' },
              { onSuccess: () => toast.success('Saved'), onError: (e) => toast.error((e as Error).message) },
            );
          setToggling(null);
        }}
      />
    </AccountingPageShell>
  );
}
